import { Injectable, inject } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import { mapSlotItem } from './slots.mapper';
import {
  SlotItem,
  SlotCapacityIntelligenceResponseDto,
  SlotCapacityMatrixResponseDto,
  SlotItemDto,
  SlotListFilters,
  SlotListPage,
  SlotListPageDto,
  SlotListSort,
} from './slots.models';

type SlotQueryFilters = SlotListFilters & {
  procedureTypeId?: string;
};

@Injectable({
  providedIn: 'root'
})
export class SlotsService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(
    filters?: SlotQueryFilters
  ): Observable<SlotItem[]>
  {
    const query = filters
      ? {
          clinicId: filters.clinicId,
          deviceId: filters.deviceId,
          specialistUserId: filters.specialistUserId,
          procedureTypeId: filters.procedureTypeId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          schedulingDate: filters.schedulingDate,
          status: filters.status
        }
      : undefined;

    return this.apiClient.get<SlotItemDto[] | null>(
      '/slots',
      query
    ).pipe(
      map((items: SlotItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map(mapSlotItem);
      })
    );
  }

  public getPage(
    params: {
      limit: number;
      cursor?: string | null;
      filters?: SlotQueryFilters;
      sort?: SlotListSort;
    },
  ): Observable<SlotListPage>
  {
    const query = {
      clinicId: params.filters?.clinicId,
      deviceId: params.filters?.deviceId,
      specialistUserId: params.filters?.specialistUserId,
      procedureTypeId: params.filters?.procedureTypeId,
      dateFrom: params.filters?.dateFrom,
      dateTo: params.filters?.dateTo,
      schedulingDate: params.filters?.schedulingDate,
      status: params.filters?.status,
      sort: params.sort,
      limit: params.limit,
      cursor: params.cursor ?? undefined,
    };

    return this.apiClient.get<SlotListPageDto>(
      '/slots',
      query,
    ).pipe(
      map((dto) => ({
        items: Array.isArray(dto?.items) ? dto.items.map(mapSlotItem) : [],
        nextCursor: dto?.nextCursor ?? null,
        hasMore: !!dto?.hasMore,
      })),
    );
  }

  public getById(id: string): Observable<SlotItem>
  {
    return this.apiClient.get<SlotItemDto>(`/slots/${id}`).pipe(
      map(mapSlotItem)
    );
  }

  public getByIds(ids: string[]): Observable<SlotItem[]>
  {
    const uniqueIds = Array.from(new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0)));
    if (uniqueIds.length === 0) {
      return of([]);
    }

    return this.apiClient.get<SlotItemDto[] | null>(
      '/slots/by-ids',
      { ids: uniqueIds.join(',') },
    ).pipe(
      map((items: SlotItemDto[] | null) => Array.isArray(items) ? items.map(mapSlotItem) : []),
    );
  }

  public getCapacityIntelligence(params: {
    clinicId: string;
    schedulingDate: string;
    procedureTypeId?: string;
  }): Observable<SlotCapacityIntelligenceResponseDto>
  {
    return this.apiClient.get<SlotCapacityIntelligenceResponseDto>(
      '/slots/capacity-intelligence',
      {
        clinicId: params.clinicId,
        schedulingDate: params.schedulingDate,
        procedureTypeId: params.procedureTypeId,
      },
    );
  }

  public getCapacityMatrix(params: {
    clinicId: string;
    schedulingDate: string;
    procedureTypeId?: string;
  }): Observable<SlotCapacityMatrixResponseDto>
  {
    return this.apiClient.get<SlotCapacityMatrixResponseDto>(
      '/slots/capacity-matrix',
      {
        clinicId: params.clinicId,
        schedulingDate: params.schedulingDate,
        procedureTypeId: params.procedureTypeId,
      },
    );
  }
}
